import { Component, OnInit } from "@angular/core";
import { CommonModule } from "@angular/common";
import { RouterModule } from "@angular/router";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { SharedComponentsModule } from "@/components/shared/shared-components.module";
import { StatsService } from "@/_services";
import { TerminologyService } from "@/_services/terminology/terminology.service";
import { TermKey } from "@/_services/terminology/catalog";
import {
    DashboardPipelineStage,
    PipelineItem,
    PipelineStage,
} from "@/_models/dashboard-stats.interface";
import { MODULE_ROUTES, STAGE_UNLOCKED_BY } from "./pipeline-routes.config";

/** A card under a column, with the route it opens. */
interface PipelineCard extends PipelineItem {
    ageLabel: string;
    routerLink: string[] | null;
}

/** One column, resolved for display. */
interface PipelineColumn {
    stage: DashboardPipelineStage;
    label: string;
    count: number;
    /** "12 hazards · 3 findings", built once rather than in the template. */
    composition: string;
    cards: PipelineCard[];
    /** How many the column holds beyond the cards shown. */
    remaining: number;
    ageLabel: string;
    alert: boolean;
    /** The plan carries none of what feeds this stage. */
    locked: boolean;
    /** Plan that would fill it, shown in place of the figures. */
    unlockedBy: string;
}

/**
 * The Safety Pipeline: every open item placed by the stage it is in (#3011).
 *
 * It answers a question nothing else on the dashboard does. The counts say how
 * much work there is; the age of the oldest item says whether it is moving.
 */
@UntilDestroy()
@Component({
    selector: "app-safety-pipeline",
    standalone: true,
    imports: [CommonModule, RouterModule, SharedComponentsModule],
    templateUrl: "./safety-pipeline.component.html",
    styleUrl: "./safety-pipeline.component.scss",
})
export class SafetyPipelineComponent implements OnInit {
    title = "";
    emptyLabel = "";
    upgradeLabel = "";
    /** False below SMS Essentials, where there is no pipeline to draw. */
    visible = false;
    columns: PipelineColumn[] = [];
    loading = true;

    constructor(
        private statsService: StatsService,
        private terminology: TerminologyService,
    ) {}

    ngOnInit(): void {
        this.title = this.term("dashboard.pipeline.title");
        this.emptyLabel = this.term("dashboard.pipeline.empty");
        this.upgradeLabel = this.term("dashboard.health.upgradeRequired");

        this.statsService
            .getDashboardStats()
            .pipe(untilDestroyed(this))
            .subscribe({
                next: (stats) => {
                    this.visible = !!stats.pipeline;
                    this.columns = (stats.pipeline ?? []).map((stage) =>
                        this.buildColumn(stage),
                    );
                    this.loading = false;
                },
                error: () => {
                    this.loading = false;
                },
            });
    }

    private buildColumn(stage: PipelineStage): PipelineColumn {
        const cards = stage.oldest.map((item) => this.buildCard(item));

        return {
            stage: stage.stage,
            label: this.term(`dashboard.pipeline.stage.${stage.stage}`),
            count: stage.count,
            composition: stage.composition
                .map(
                    (part) =>
                        `${part.count} ${this.term(
                            `dashboard.module.${part.key}`,
                        )}`,
                )
                .join(" · "),
            cards,
            remaining: Math.max(0, stage.count - cards.length),
            ageLabel: this.ageLabel(stage.oldestAgeDays),
            alert: stage.alert,
            locked: stage.locked,
            unlockedBy: STAGE_UNLOCKED_BY[stage.stage] ?? "",
        };
    }

    /**
     * A card opens the record it stands for, resolved from the module that
     * produced it rather than from the column, which can be fed by several.
     */
    private buildCard(item: PipelineItem): PipelineCard {
        const route = MODULE_ROUTES[item.moduleKey];

        return {
            ...item,
            ageLabel: this.ageLabel(item.ageDays),
            routerLink: route ? [...route, item.id] : null,
        };
    }

    private ageLabel(days: number | null): string {
        if (days === null) {
            return "";
        }
        if (days === 0) {
            return this.term("dashboard.pipeline.ageToday");
        }
        const unit = this.term(
            days === 1
                ? "dashboard.pipeline.ageDay"
                : "dashboard.pipeline.ageDays",
        );
        return `${days} ${unit}`;
    }

    private term(key: string): string {
        return this.terminology.resolve(key as TermKey);
    }
}
